import React, { useEffect } from 'react';

import type { SSRInput } from 'utils/ssr';
import { getSSRInput } from 'utils/ssr';
import {
    ConsentBanner,
    initializeControls,
    getCookie,
    ATLASSIAN_CONSENT_COOKIE_KEY,
    ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
    shouldRenderBannerPrefetched,
} from '@atlassian/browser-storage-controls';
import type { PrefetchedConsentPreferenceData } from '@atlassian/browser-storage-controls';

const JSMCookieConsentBanner = () => {
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        initializeControls({ product: 'jira' });
    }, []);

    return <ConsentBanner product="jira" />;
};

export default JSMCookieConsentBanner;

export const SSRSafeJSMCookieConsentBanner = () => {
    const ssrInput: SSRInput | undefined = getSSRInput();
    const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY) || ssrInput?.cookiesConsentToken;
    const fallbackConsentToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY) || ssrInput?.cookiesFallbackToken;

    const cookieData: PrefetchedConsentPreferenceData = consentToken
        ? {
              consentToken,
              fallbackConsentToken,
          }
        : {
              fallbackConsentToken,
          };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        initializeControls({ product: 'jira' });
    }, []);

    // The case of only the fallbackConsentToken being present is handled differently on the
    // server and on the client. So in order to SSR the same way, we have handle this case
    // specially. Only fallback token being present on the client results in banner not being shown.
    // However, if we pass that cookie data into the initialPreferences on the client, there is a short
    // flicker of the consent banner. So we also have to special case this on the client.
    const onlyFallbackTokenPresent = Boolean(fallbackConsentToken && !consentToken);

    if (__SERVER__) {
        const shouldRenderBanner = onlyFallbackTokenPresent ? false : shouldRenderBannerPrefetched(cookieData);
        if (shouldRenderBanner) {
            return (
                <div data-testid={'jsm-cookie-consent-banner-wrapper'}>
                    <ConsentBanner product="jira" initialPreferences={cookieData} />
                </div>
            );
        }
        return <div data-testid={'jsm-cookie-consent-banner-wrapper'}></div>;
    }

    return (
        // Wrapper needed for stable DOM to rehydrate. Otherwise, rehydration failures may break the entire page when
        // this returns a banner in SSR and no banner/null in CSR (or the other way around).
        <div data-testid={'jsm-cookie-consent-banner-wrapper'}>
            {onlyFallbackTokenPresent ? (
                <ConsentBanner product="jira" />
            ) : (
                <ConsentBanner product="jira" initialPreferences={cookieData} />
            )}
        </div>
    );
};
